import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './LoginPage.css';

const COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'FR', label: 'France' },
  { code: 'DE', label: 'Germany' },
  { code: 'AU', label: 'Australia' },
  { code: 'IN', label: 'India' },
  { code: 'SG', label: 'Singapore' },
  { code: 'JP', label: 'Japan' },
  { code: 'BR', label: 'Brazil' },
  { code: 'NL', label: 'Netherlands' },
  { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' },
  { code: 'SE', label: 'Sweden' },
  { code: 'CH', label: 'Switzerland' },
];

function LoginPage({ onLoginSuccess, mode = 'login', redirectTo = null }) {
  const navigate = useNavigate();
  const isAddSender = mode === 'add-sender';
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    country: 'US'
  });
  const [checkpoint, setCheckpoint] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pollingStatus, setPollingStatus] = useState('');
  const pollingIntervalRef = useRef(null);
  const pollingStartTimeRef = useRef(null);

  const finishLogin = (id) => {
    if (onLoginSuccess) onLoginSuccess(id);
    if (redirectTo) navigate(redirectTo);
  };

  const handleCancel = () => {
    stopPolling();
    setCheckpoint(null);
    setVerificationCode('');
    setFormData({ username: '', password: '', country: 'US' });
    if (isAddSender) navigate('/senders');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/linkedin/login`, {
        email: formData.username,
        password: formData.password,
        country: formData.country
      });

      if (response.data.countryWarning) {
        setError(response.data.countryWarning);
      }

      if (response.data.success && response.data.requiresCheckpoint) {
        // 需要验证
        const checkpointData = {
          type: response.data.checkpointType,
          account_id: response.data.accountId
        };
        setCheckpoint(checkpointData);
        setSuccess(response.data.message);

        // 如果是 IN_APP_VALIDATION，开始轮询账号状态
        if (response.data.checkpointType === 'IN_APP_VALIDATION') {
          startPollingAccountStatus(checkpointData.account_id);
        }
      } else if (response.data.success && !response.data.requiresCheckpoint) {
        // 登录成功
        setSuccess('登录成功！');
        setTimeout(() => {
          finishLogin(response.data.accountId);
        }, 1000);
      }
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.errorType === 'browser_auth_required') {
        setError(err.response.data.message);
      } else if (err.response?.status === 409 && err.response?.data?.isDuplicate) {
        setError(`⚠️ ${err.response.data.message}\n账号ID: ${err.response.data.accountId}\n\n请不要重复连接相同的LinkedIn账号。如需更换账号，请先删除已有连接。`);
      } else {
        setError(err.response?.data?.message || '登录失败，请检查邮箱和密码');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSolveCheckpoint = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(`${API_BASE_URL}/linkedin/solve-checkpoint`, {
        accountId: checkpoint.account_id,
        checkpointType: checkpoint.type,
        code: verificationCode
      });

      if (response.data.success) {
        // 验证成功
        const accountId = response.data.accountId || checkpoint.account_id;
        if (!accountId) {
          setError('验证成功但未返回账号ID，请重试');
          setLoading(false);
          return;
        }
        console.log('✅ Checkpoint solved, accountId:', accountId);
        setSuccess('验证成功！');
        setTimeout(() => {
          finishLogin(accountId);
        }, 1000);
      }
    } catch (err) {
      setError(err.response?.data?.message || '验证失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 检查账号状态的核心逻辑
  const checkAccountStatus = async (accountId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/linkedin/account-status/${accountId}`);

      if (response.data.success && response.data.status) {
        const account = response.data.status;
        let isConnected = false;

        // 1. 检查顶层状态
        const topLevelStatus = account.status?.toUpperCase();
        if (topLevelStatus === 'OK' || topLevelStatus === 'CONNECTED') {
          isConnected = true;
        }

        // 2. 检查 sources 数组 (Unipile 返回的结构可能包含 sources)
        if (!isConnected && Array.isArray(account.sources)) {
          isConnected = account.sources.some(source =>
            source.status === 'OK' || source.status === 'CONNECTED'
          );
        }

        if (isConnected) {
          // 登录成功！验证账号并删除重复
          stopPolling();
          setSuccess('验证成功！正在登录...');

          // 调用验证端点以删除重复账号
          try {
            const verifyResponse = await axios.post(`${API_BASE_URL}/linkedin/verify-account/${accountId}`);
            const finalAccountId = verifyResponse.data.accountId || accountId;

            console.log(`✅ Account verified. Using account ID: ${finalAccountId}`);
            setTimeout(() => {
              finishLogin(finalAccountId);
            }, 1000);
          } catch (err) {
            console.error('Account verification failed:', err);
            // 即使验证失败，仍然继续登录
            setTimeout(() => {
              finishLogin(accountId);
            }, 1000);
          }

          return true; // Success
        } else if (account.status === 'CHECKPOINT') {
          // 仍在等待验证
          if (pollingStartTimeRef.current) {
            const elapsed = (Date.now() - pollingStartTimeRef.current) / 1000;
            const remaining = Math.max(0, 300 - elapsed);
            setPollingStatus(`等待确认中... (剩余 ${Math.floor(remaining)} 秒)`);
          } else {
            setPollingStatus('等待确认中...');
          }
        } else {
          // 其他状态
          const statusText = account.status || (account.sources && account.sources[0]?.status) || 'UNKNOWN';
          const checkpointStatus = account.checkpoint_status || 'N/A';
          setPollingStatus(`状态: ${statusText} (Checkpoint: ${checkpointStatus})`);
        }
      }
    } catch (err) {
      console.error('Polling error:', err);
      // 继续轮询，不中断
    }
    return false;
  };

  // 手动检查状态
  const handleManualCheck = async () => {
    if (!checkpoint?.account_id) return;

    setPollingStatus('正在手动检查状态...');
    await checkAccountStatus(checkpoint.account_id);
  };

  // 轮询账号状态（用于 IN_APP_VALIDATION）
  const startPollingAccountStatus = (accountId) => {
    // 清除之前的轮询
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingStartTimeRef.current = Date.now();
    setPollingStatus('等待确认中...');

    // 立即检查一次
    checkAccountStatus(accountId);

    // 每2秒检查一次账号状态
    pollingIntervalRef.current = setInterval(async () => {
      // 检查是否超时（5分钟）
      const elapsed = (Date.now() - pollingStartTimeRef.current) / 1000;
      if (elapsed > 300) {
        stopPolling();
        setError('验证超时（5分钟），请重新登录');
        setCheckpoint(null);
        return;
      }

      await checkAccountStatus(accountId);
    }, 2000); // 每2秒检查一次
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPollingStatus('');
  };

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const getCheckpointMessage = (type) => {
    const messages = {
      '2FA': '请输入您的两步验证码',
      'OTP': '请输入发送到您邮箱或手机的验证码',
      'IN_APP_VALIDATION': '请在LinkedIn移动应用中确认登录',
      'CAPTCHA': '需要完成验证码验证',
      'PHONE_REGISTER': '请输入您的手机号码（格式：(+国家代码)号码）'
    };
    return messages[type] || '需要完成验证';
  };

  return (
    <div className="page-container">
      <div className="card">
        <h1>{isAddSender ? '🔗 连接新 LinkedIn 账号' : '🔐 LinkedIn 登录'}</h1>
        <p>
          {isAddSender
            ? '输入另一个 LinkedIn 账号信息以添加为新的 sender'
            : '请输入您的LinkedIn账号信息'}
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {!checkpoint ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="username">LinkedIn 邮箱</label>
              <input
                type="email"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                placeholder="example@email.com"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">密码</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="输入您的密码"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="country">账号所在国家</label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                disabled={loading}
              >
                {COUNTRY_OPTIONS.map(opt => (
                  <option key={opt.code} value={opt.code}>
                    {opt.code} - {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  登录中...
                </>
              ) : (
                isAddSender ? '连接账号' : '登录 LinkedIn'
              )}
            </button>

            {isAddSender && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
                style={{ marginTop: '10px' }}
              >
                取消
              </button>
            )}
          </form>
        ) : (
          <div className="checkpoint-container">
            <div className="alert alert-info">
              <strong>验证类型：</strong> {checkpoint.type}<br />
              {getCheckpointMessage(checkpoint.type)}
            </div>

            {checkpoint.type === 'IN_APP_VALIDATION' ? (
              <div className="in-app-validation">
                <p>请在您的LinkedIn移动应用中确认此次登录。</p>
                <p>确认后，页面将自动跳转。</p>
                {pollingStatus && (
                  <div className="alert alert-info" style={{ marginTop: '15px' }}>
                    {pollingStatus}
                  </div>
                )}
                <button
                  className="btn btn-primary"
                  style={{ marginTop: '10px', marginBottom: '10px', width: '100%' }}
                  onClick={handleManualCheck}
                >
                  我已在手机上确认
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleCancel}
                >
                  {isAddSender ? '取消并返回' : '返回重新登录'}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSolveCheckpoint}>
                <div className="form-group">
                  <label htmlFor="verificationCode">
                    {checkpoint.type === 'PHONE_REGISTER' ? '手机号码' : '验证码'}
                  </label>
                  <input
                    type="text"
                    id="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    placeholder={
                      checkpoint.type === 'PHONE_REGISTER'
                        ? '(+86)13812345678'
                        : '输入验证码'
                    }
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      验证中...
                    </>
                  ) : (
                    '提交验证'
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  {isAddSender ? '取消并返回' : '返回重新登录'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;


